import React, { useState } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Mail, Phone, Send, Check, Loader2, AlertCircle, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const ConnectSection = () => {
  const { t, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send message');
      }

      setIsSubmitted(true);
      toast.success(t.connect.form.success);

      setTimeout(() => {
        setFormData({ name: '', email: '', subject: '', message: '' });
        setIsSubmitted(false);
      }, 5000);
    } catch (err) {
      console.error('Contact form error:', err);
      setError(err.message || 'Failed to send message. Please try again.');
      toast.error(isRTL ? 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="connect" className="section-padding bg-charcoal-100">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-20">

          {/* ── Info Side ── */}
          <div className={isRTL ? 'lg:order-2' : 'lg:order-1'}>
            <p className="text-gold text-xs uppercase tracking-[0.2em] sm:tracking-[0.3em] mb-3 sm:mb-4">
              {t.connect.tagline}
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl xl:text-5xl text-cream leading-tight mb-4 sm:mb-6">
              {t.connect.headline}
            </h2>
            <div className="gold-divider !mx-0 !ml-0 mb-6 sm:mb-8" />

            {/* Description — slightly more visual weight */}
            <p className="text-cream text-sm sm:text-base leading-relaxed font-light mb-8 sm:mb-10">
              {t.connect.description}
            </p>

            {/* Contact cards */}
            <div className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
              <a
                href={`mailto:${t.connect.email}`}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border border-white/10 hover:border-gold/30 transition-colors group active:scale-98 rounded-xl"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border border-gold/30 bg-gold/10 group-hover:bg-gold/20 transition-colors flex-shrink-0 rounded-lg">
                  <Mail size={16} className="text-gold sm:w-[18px] sm:h-[18px]" />
                </div>
                <div className="min-w-0">
                  <p className="text-gold text-xs uppercase tracking-wider mb-0.5 sm:mb-1">
                    {isRTL ? 'البريد الإلكتروني' : 'Email'}
                  </p>
                  <p className="text-cream text-sm sm:text-base truncate">{t.connect.email}</p>
                </div>
              </a>

              <a
                href={`tel:${t.connect.phone}`}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border border-white/10 hover:border-gold/30 transition-colors group active:scale-98 rounded-xl"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center border border-gold/30 bg-gold/10 group-hover:bg-gold/20 transition-colors flex-shrink-0 rounded-lg">
                  <Phone size={16} className="text-gold sm:w-[18px] sm:h-[18px]" />
                </div>
                <div>
                  <p className="text-gold text-xs uppercase tracking-wider mb-0.5 sm:mb-1">
                    {isRTL ? 'الهاتف' : 'Phone'}
                  </p>
                  <p className="text-cream text-sm sm:text-base">{t.connect.phone}</p>
                </div>
              </a>
            </div>

            {/* Work With Mona — improved chip visibility */}
            <div className="p-4 sm:p-6 bg-charcoal border border-gold/15 rounded-xl">
              <h4 className="text-gold text-xs uppercase tracking-wider mb-3 sm:mb-4">
                {t.connect.workWith.title}
              </h4>
              <div className="flex flex-wrap gap-2">
                {t.connect.workWith.items.map((item, index) => (
                  <span
                    key={index}
                    className="px-2.5 py-1 sm:px-3 sm:py-1.5 text-cream-300 text-xs border border-gold/20 bg-gold/5 rounded-md"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* ── Form Side ── */}
          <div className={isRTL ? 'lg:order-1' : 'lg:order-2'}>
            <div className="p-5 sm:p-8 lg:p-10 bg-charcoal border border-gold/15 rounded-2xl">

              {/* Form header */}
              <div className="mb-6 sm:mb-8">
                <div className="flex items-center gap-2 mb-1">
                  <MessageCircle size={14} className="text-gold" />
                  <p className="text-gold text-xs uppercase tracking-wider">
                    {isRTL ? 'أرسل رسالة' : 'Send a message'}
                  </p>
                </div>
                <p className="text-cream-400 text-xs leading-relaxed">
                  {isRTL
                    ? 'أقرأ كل رسالة بشكل شخصي وأرد خلال 48 ساعة.'
                    : 'Every message is read personally. I respond within 48 hours.'}
                </p>
              </div>

              {/* Success state — full panel */}
              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-14 sm:py-20 text-center">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gold/10 border border-gold/30 mb-5">
                    <Check size={28} className="text-gold" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-cream mb-3">
                    {isRTL ? 'شكراً لك!' : 'Thank you!'}
                  </h3>
                  <p className="text-cream-300 text-sm leading-relaxed max-w-xs">
                    {t.connect.form.success}
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="space-y-4 sm:space-y-5">

                    {/* Name */}
                    <div>
                      <label className="block text-gold text-xs uppercase tracking-wider mb-2">
                        {t.connect.form.name}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        autoComplete="name"
                        placeholder={isRTL ? 'اسمك الكامل' : 'Your full name'}
                        className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 text-cream text-sm placeholder:text-cream-400/50 focus:border-gold focus:outline-none transition-colors rounded-lg"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-gold text-xs uppercase tracking-wider mb-2">
                        {t.connect.form.email}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        autoComplete="email"
                        placeholder={isRTL ? 'بريدك@الإلكتروني.com' : 'your@email.com'}
                        className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 text-cream text-sm placeholder:text-cream-400/50 focus:border-gold focus:outline-none transition-colors rounded-lg"
                      />
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-gold text-xs uppercase tracking-wider mb-2">
                        {t.connect.form.subject}
                      </label>
                      <select
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 text-cream text-sm focus:border-gold focus:outline-none transition-colors cursor-pointer rounded-lg"
                      >
                        <option value="" className="text-cream-400">
                          {isRTL ? 'ما الذي تودّ مناقشته؟' : 'What would you like to discuss?'}
                        </option>
                        {t.connect.form.subjectOptions.map((option, index) => (
                          <option key={index} value={option} className="bg-charcoal">
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-gold text-xs uppercase tracking-wider mb-2">
                        {t.connect.form.message}
                      </label>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={4}
                        placeholder={isRTL
                          ? 'أخبريني عن نفسك وعما تبحثين عنه...'
                          : 'Tell me a little about yourself and what you\'re looking for...'}
                        className="w-full px-4 py-3 bg-charcoal-100 border border-white/10 text-cream text-sm placeholder:text-cream-400/50 focus:border-gold focus:outline-none transition-colors resize-none rounded-lg"
                      />
                    </div>

                    {/* Error state */}
                    {error && (
                      <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                        <AlertCircle size={15} className="flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </div>
                    )}

                    {/* Submit button + reassurance */}
                    <div>
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full btn-gold flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed active:scale-[0.98]"
                      >
                        {isSubmitting ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            <span>{isRTL ? 'جاري الإرسال...' : 'Sending...'}</span>
                          </>
                        ) : (
                          <>
                            <Send size={15} />
                            <span>{t.connect.form.submit}</span>
                          </>
                        )}
                      </button>
                      <p className="text-cream-400 text-xs text-center mt-3">
                        {isRTL
                          ? 'لن يتم مشاركة معلوماتك مع أي جهة خارجية.'
                          : 'Your information is never shared with anyone.'}
                      </p>
                    </div>

                  </div>
                </form>
              )}

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ConnectSection;
